import { NotificationNew } from '@thiememeulenhoff/types';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { inject } from '@angular/core';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { filter, of, pipe, switchMap, tap } from 'rxjs';
import { tapResponse } from '@ngrx/operators';
import { NotificationNewService } from '@thiememeulenhoff/ng-api-gateway';
import { withRequestStatus } from '../_utils/request-status.feature';

type NotificationState = {
  notifications: Array<NotificationNew>;
};

const initialState: NotificationState = {
  notifications: [],
};

export const NotificationNewStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withRequestStatus(),
  withMethods(
    (store, notificationService = inject(NotificationNewService)) => ({
      fetchNotifications: rxMethod<string | undefined>(
        pipe(
          filter((methodSlug): methodSlug is string => !!methodSlug),
          tap(() => patchState(store, { requestStatus: 'loading' })),
          switchMap((methodSlug) =>
            notificationService.fetchNotifications(methodSlug).pipe(
              tapResponse({
                next: (notifications) =>
                  patchState(store, {
                    notifications,
                    requestStatus: 'success',
                  }),
                error: (error: Error) =>
                  patchState(store, {
                    requestStatus: {
                      error: error.message || 'Failed to fetch',
                    },
                  }),
              }),
            ),
          ),
        ),
      ),
      markAsSeen: rxMethod<string>(
        pipe(
          switchMap((notificationId) =>
            notificationService.postNotificationSeen(notificationId).pipe(
              tap(() => {
                patchState(store, (state) => ({
                  notifications: state.notifications.map((n) =>
                    n.id === notificationId ? { ...n, seen: true } : n,
                  ),
                }));
              }),
            ),
          ),
        ),
      ),
      deleteNotification: rxMethod<string>(
        pipe(
          switchMap((notificationId) =>
            notificationService.deleteNotification(notificationId).pipe(
              tap(() => {
                patchState(store, (state) => ({
                  notifications: state.notifications.filter(
                    (n) => n.id !== notificationId,
                  ),
                }));
              }),
            ),
          ),
        ),
      ),
      markMethodNotificationsAsSeen: rxMethod<{
        methodSlug: string | undefined;
        untilNotificationId: string;
      }>(
        pipe(
          switchMap(({ methodSlug, untilNotificationId }) => {
            if (!methodSlug) {
              return of();
            }
            return notificationService
              .postNotificationAllSeen(methodSlug, untilNotificationId)
              .pipe(
                tap(() => {
                  patchState(store, (state) => ({
                    notifications: state.notifications.map((n) =>
                      n.methodSlug === methodSlug ? { ...n, seen: true } : n,
                    ),
                  }));
                }),
              );
          }),
        ),
      ),
    }),
  ),
);
